import React, { useMemo } from 'react';
import { translate } from '../../Services/translation';
import ProductCard from './ui/ProductCard';
import { breakpoint } from '../../styling/mixins/breakpoints';
import styled from 'styled-components';
import { getColor } from '../../styling/mixins/colors';
import { text, fontFamilyBold } from '../../styling/mixins/typography';
import { grid, cells, section } from '../../styling/utils/grid';
import { Order } from '../../Types/Order';
import { EditorStringModel } from '../../Types/EditorString';
import SalesAddress from '../../Types/OrderDetailAddress';
declare var LitiumConstants: any;

interface OrderDetailProps {
  order: Order;
  isConfirmation: boolean;
  confirmationTitle?: string;
  confirmationText?: EditorStringModel;
  confirmationExtraText?: EditorStringModel;
}

const StyledOrderDetail = styled.div`
  ${section}
  display: flex;
  flex-direction: column;
  background-color: ${getColor('white')};
  padding-top: 1.6rem;
  padding-bottom: 2.4rem;
  gap: 1.6rem;
  ${breakpoint('large')} {
    padding-top: 3.2rem;
    padding-bottom: 3.2rem;
  }
  ${breakpoint('xlarge')} {
    padding-top: 4.8rem;
    padding-bottom: 4.8rem;
  }

  &.confirmation-detail {
    ${breakpoint('large')} {
      padding-top: 4.8rem;
      padding-bottom: 4.8rem;
      align-items: center;
    }
  }

  &.my-pages-detail {
    margin: 0 -0.8rem;
    margin-bottom: -2.4rem;

    ${breakpoint('medium')} {
      margin: 0 -1.2rem;
      margin-bottom: -2.4rem;
    }

    ${breakpoint('large')} {
      align-items: center;
      margin: unset;
    }
  }

  .title {
    ${fontFamilyBold}
    text-transform: uppercase;
    margin-bottom: 0.8rem;
  }

  .msg-title {
    ${fontFamilyBold}
  }

  .top-wrapper {
    display: flex;
    flex-direction: column;
    ${text('large')}
    gap: 0.8rem;

    ${breakpoint('large')} {
      gap: 2.4rem;
      flex-direction: row;
    }
  }

  .top-span {
    ${fontFamilyBold}
  }

  .top-msg {
    ${text('large')}
    margin-top: 0.8rem;
  }

  .block {
    background: ${getColor('white')};
    padding: 1.6rem;
    border: 1px solid ${getColor('gray', 200)};
    ${breakpoint('large')} {
      width: 100%;
      max-width: 660px;
      padding: 2.4rem;
    }
  }

  .main-block {
    display: flex;
    flex-direction: column;
    padding: 0;
  }

  .msg-block {
    ${text('large')}
  }

  .bottom-block {
    ${grid}
    gap: 2.4rem;
    flex-wrap: wrap;

    ${breakpoint('large')} {
      ${cells(2)}
    }
  }

  .price-wrapper {
    ${text('large')}
    padding: 1.6rem;

    ${breakpoint('large')} {
      padding: 2.4rem;
    }

    & div:not(:last-child) {
      margin-bottom: 0.8rem;
    }
  }

  .price {
    display: flex;
    justify-content: space-between;
  }

  .saved-price {
    color: ${getColor('alert')};
  }

  .total-price {
    ${fontFamilyBold}
  }

  .bottom-cell {
    ${text('large')}
    display: flex;
    flex-direction: column;
    text-transform: capitalize;
  }

  .bottom-span {
    ${fontFamilyBold}
    margin-bottom: 0.8rem;
  }
`;

const OrderDetailAddress = ({ address }: { address: SalesAddress }) => {
  const addressCountry =
    LitiumConstants.countries && address
      ? LitiumConstants.countries.find(
          (country) =>
              country.value && address.country && country.value.toLowerCase() === address.country.toLowerCase()
        )
      : null;

  return (
    <>
      {address.organizationName && <span>{address.organizationName}</span>}
      <span>
        {address.firstName} {address.lastName}
      </span>
      <span>{address.address1}</span>
      {address.address2 && <span>{address.address2}</span>}
      {address.careOf && <span>C/O: {address.careOf}</span>}
      <span>{address.city}</span>
      <span>{addressCountry ? addressCountry.text : address.country}</span>
      <span>{address.zipCode}</span>
    </>
  );
};

const OrderDetail = ({
  order,
  isConfirmation,
  confirmationTitle,
  confirmationText,
  confirmationExtraText,
}: OrderDetailProps) => {
  const deliveryAddress = useMemo(
    () => (order.deliveries?.length ? order.deliveries[0].address : null),
    [order.deliveries]
  );

  return (
    <StyledOrderDetail
      className={isConfirmation ? 'confirmation-detail' : 'my-pages-detail'}
    >
      {isConfirmation ? (
        <div className="block">
          <h1 className="title">{confirmationTitle}</h1>
          <div className="top-wrapper">
            <span>
              {translate('general.ordernumber')}:
              <span className="top-span"> {order.externalOrderID}</span>
            </span>
            <span>
              {translate('orderdetail.orderdate')}:
              <span className="top-span"> {order.orderDate}</span>
            </span>
          </div>
          {confirmationText?.value && (
            <div
              className="top-msg"
              dangerouslySetInnerHTML={{
                __html: confirmationText?.value,
              }}
            ></div>
          )}
        </div>
      ) : (
        <div className="block">
          <h1 className="title">
            {translate('general.ordernumber')}: {order.externalOrderID}
          </h1>
          <div className="top-wrapper">
            <span>
              {translate('orderdetail.orderdate')}:
              <span className="top-span"> {order.orderDate}</span>
            </span>
            <span>
              {translate('orderdetail.orderstatus')}:
              <span className="top-span"> {order.status}</span>
            </span>
          </div>
        </div>
      )}
      <div className="block main-block">
        {order.orderRows.map((product) => (
          <ProductCard product={product} key={product.deliveryId} />
        ))}
        <div className="price-wrapper">
          <div className="price">
            <span>{translate('checkout.order.sum')}</span>
            <span>{order.orderTotalWithVat}</span>
          </div>
          {order.orderTotalDiscountAmount && (
            <div className="price saved-price">
              <span>{translate('checkout.order.saved')}</span>
              <span>{order.orderTotalDiscountAmount}</span>
            </div>
          )}
          <div className="price">
            <span>{translate('checkout.order.shipping')}</span>
            <span>{order.orderTotalDeliveryCost}</span>
          </div>
          {order.orderTotalFee && (
            <div className="price">
              <span>{translate('checkout.order.fee')}</span>
              <span>{order.orderTotalFee}</span>
            </div>
          )}
          <div className="price total-price">
            <span>{translate('orderdetail.grandtotal')}</span>
            <span>{order.orderGrandTotal}</span>
          </div>
          <div className="price">
            <span>{translate('checkout.order.vat')}</span>
            <span>{order.orderTotalVat}</span>
          </div>
        </div>
      </div>
      <div className="block bottom-block">
        {deliveryAddress && (
          <div className="bottom-cell">
            <span className="bottom-span">
              {translate('orderdetail.deliveryaddress')}
            </span>
            <OrderDetailAddress address={deliveryAddress} />
          </div>
        )}
        {order.billingAddress && (
          <div className="bottom-cell">
            <span className="bottom-span">
              {translate('orderdetail.billingadress')}
            </span>
            <OrderDetailAddress address={order.billingAddress} />
          </div>
        )}
        <div className="bottom-cell">
          <span className="bottom-span">
            {translate('orderdetail.paymentmethod')}
          </span>
          <span>{order.paymentMethod}</span>
        </div>
        <div className="bottom-cell">
          <span className="bottom-span">
            {translate('orderdetail.deliverymethod')}
          </span>
          <span>{order.deliveries && order.deliveries.length > 0 ? order.deliveries[0].deliveryMethodTitle : order.deliveryMethod}</span>
        </div>
        {order?.comments && order.comments != '' && (
          <div className="bottom-cell">
            <span className="bottom-span">
              {translate('checkout.order.message')}
            </span>
            <span>{order.comments}</span>
          </div>
        )}
      </div>
      {isConfirmation && confirmationExtraText?.value && (
        <div
          className="block msg-block"
          dangerouslySetInnerHTML={{
            __html: confirmationExtraText?.value,
          }}
        ></div>
      )}
    </StyledOrderDetail>
  );
};

export default OrderDetail;
